import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert } from '@mui/material';
import { Theme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';

import {
  AlphaFieldRenderer,
  LoadOptions,
  MAX_OPTIONS,
  ReadOnlyValue,
  collectErrors,
  isWideField,
  toSubmitValues,
  withDefaults,
} from './AlphaFieldRenderer';
import { readCardDraft, writeCardDraft } from './cardDraft';
import { focusMayMove } from './cardFocusGuard';
import {
  Visibility,
  changedKeys,
  flattenFields,
  getPath,
  isRecord,
  setPath,
  visibilityOf,
} from './cardModel';
import {
  BlockedNote,
  CardFooter,
  CardFrame,
  CardHeader,
  CardLead,
  CardRoot,
  CardTitle,
  ChangedChip,
  EmptyLine,
  FieldCell,
  FieldGrid,
  FooterSpacer,
  LinkButton,
  NotSet,
  PrimaryButton,
  ReviewGroup,
  ReviewGroupHeader,
  ReviewGroupTitle,
  ReviewList,
  SecondaryButton,
  StepAttention,
  StepBody,
  StepChip,
  StepDot,
  StepHeading,
  StepList,
  StepState,
  VisuallyHidden,
} from './cardStyles';
import {
  AlphaCard,
  CardHandlers,
  CardServerError,
  FieldErrors,
  FormField,
  FormOption,
  FormStep,
} from './cardTypes';
import {
  OptionLabels,
  displayLines,
  isBlank,
  scalar,
  toList,
} from './fieldDisplay';
import {
  ANOTHER_REQUEST_TEXT,
  CardFailureAlert,
  SUBMIT_FAILED_TEXT,
  addOnce,
  belongsTo,
  useCardActions,
  useCardFocus,
} from './useCardActions';

export interface AlphaFormCardProps extends CardHandlers {
  token: string;
  card: AlphaCard;
  serverError?: CardServerError;
  submitting?: boolean;
  cancelling?: boolean;
  blocked?: boolean;
  blockedMessage?: string;
  cancelBlocked?: boolean;
}

interface ChipModel {
  key: string;
  title: string;
  state: StepState;
  current: boolean;
  navigable: boolean;
}

const LEAD = 'Alpha filled in what it knew. Check each step.';
const SUBMIT_FAILED = SUBMIT_FAILED_TEXT.form;
const CANCEL_FAILED = "Couldn't cancel the form. Try again.";
const REVIEW_KEY = 'alpha-review';

const attentionText = (count: number): string =>
  count === 1 ? '1 step needs attention' : `${count} steps need attention`;

const hasErrors = (errors: FieldErrors): boolean =>
  Object.keys(errors).length > 0;

const errorsFor = (fields: FormField[], errors: FieldErrors): FieldErrors =>
  Object.fromEntries(
    Object.entries(errors).filter(([key]) =>
      fields.some(field => belongsTo(key, field.key)),
    ),
  );

const onlyKeys = (errors: FieldErrors, keys: string[]): FieldErrors =>
  Object.fromEntries(
    Object.entries(errors).filter(([key]) =>
      keys.some(touched => belongsTo(key, touched)),
    ),
  );

const clamp = (value: number, max: number): number =>
  Math.min(Math.max(value, 0), max);

const firstErrorStep = (steps: FormStep[], errors?: FieldErrors): number =>
  errors == null || !hasErrors(errors)
    ? -1
    : steps.findIndex(step => hasErrors(errorsFor(step.fields, errors)));

const StepChips = ({
  chips,
  onSelect,
}: {
  chips: ChipModel[];
  onSelect: (index: number) => void;
}): JSX.Element => (
  <StepList aria-label="Steps">
    {chips.map((chip, index) => (
      <li key={chip.key}>
        <StepChip
          type="button"
          state={chip.state}
          current={chip.current}
          disabled={!chip.navigable}
          aria-current={chip.current ? 'step' : undefined}
          onClick={() => {
            onSelect(index);
          }}
        >
          <span>{`${index + 1} ${chip.title}`}</span>
          {chip.state === 'done' && (
            <CheckIcon
              sx={{
                fontSize: (theme: Theme): string | number | undefined =>
                  theme.typography.body2.fontSize,
              }}
            />
          )}
          {chip.state === 'error' && <StepDot aria-hidden="true" />}
          {chip.state === 'done' && <VisuallyHidden>, done</VisuallyHidden>}
          {chip.state === 'error' && (
            <VisuallyHidden>, needs attention</VisuallyHidden>
          )}
        </StepChip>
      </li>
    ))}
  </StepList>
);

const ReviewStep = ({
  steps,
  values,
  visibility,
  labels,
  changed,
  disabled,
  prefill,
  loadOptions,
  onOptions,
  onEdit,
}: {
  steps: FormStep[];
  visibility: Visibility;
  values: Record<string, unknown>;
  labels: OptionLabels;
  changed: string[];
  disabled: boolean;
  prefill: Record<string, unknown>;
  loadOptions: LoadOptions;
  onOptions: (field: FormField, options: FormOption[]) => void;
  onEdit: (index: number) => void;
}): JSX.Element => (
  <>
    {steps.map((step, index) => {
      const shown = step.fields.filter(
        field => field.type !== 'note' && visibility.field(field),
      );
      return (
        <ReviewGroup key={step.key}>
          <ReviewGroupHeader>
            <ReviewGroupTitle>{step.title}</ReviewGroupTitle>
            <LinkButton
              type="button"
              disabled={disabled}
              aria-label={`Edit ${step.title}`}
              onClick={() => {
                onEdit(index);
              }}
            >
              Edit
            </LinkButton>
          </ReviewGroupHeader>
          {shown.length === 0 ? (
            <EmptyLine>Nothing to fill in here.</EmptyLine>
          ) : (
            <ReviewList>
              {shown.map(field => {
                const lines = displayLines(
                  field,
                  getPath(values, field.key),
                  labels,
                  visibility,
                );
                return (
                  <React.Fragment key={field.key}>
                    <dt>
                      {field.label}
                      {changed.includes(field.key) && (
                        <ChangedChip>Changed</ChangedChip>
                      )}
                    </dt>
                    <dd>
                      {field.readOnly === true && (
                        <ReadOnlyValue
                          field={field}
                          value={getPath(values, field.key)}
                          loadOptions={loadOptions}
                          onOptions={onOptions}
                          scope={visibility}
                          prefill={prefill}
                        />
                      )}
                      {field.readOnly !== true &&
                        (lines.length === 0 ? (
                          <NotSet>Not set</NotSet>
                        ) : (
                          lines.join('\n')
                        ))}
                    </dd>
                  </React.Fragment>
                );
              })}
            </ReviewList>
          )}
        </ReviewGroup>
      );
    })}
  </>
);

export const AlphaFormCard = ({
  token,
  card,
  serverError,
  submitting,
  cancelling: cancellingElsewhere,
  blocked = false,
  blockedMessage = ANOTHER_REQUEST_TEXT,
  cancelBlocked = false,
  loadOptions,
  onSubmit,
  onCancel,
}: AlphaFormCardProps): JSX.Element => {
  const steps = useMemo(() => card.form?.steps ?? [], [card.form]);
  const fields = useMemo(
    () => (card.form != null ? flattenFields(card.form) : []),
    [card.form],
  );
  const reviewIndex = steps.length;
  const prefill = useMemo(
    () => (isRecord(card.values) ? card.values : {}),
    [card.values],
  );
  const initialValues = useMemo(
    () => withDefaults(fields, prefill),
    [fields, prefill],
  );
  const baseline = useMemo(
    () =>
      isRecord(card.original)
        ? withDefaults(fields, card.original)
        : initialValues,
    [card.original, fields, initialValues],
  );
  const [draft] = useState(() => readCardDraft(token));
  const [values, setValues] = useState<Record<string, unknown>>(
    () => draft?.values ?? initialValues,
  );
  const visibility = visibilityOf(fields, values);
  const [step, setStep] = useState(() => clamp(draft?.step ?? 0, reviewIndex));
  const [furthest, setFurthest] = useState(() =>
    clamp(Math.max(draft?.furthest ?? 0, draft?.step ?? 0), reviewIndex),
  );
  const [attempted, setAttempted] = useState<number[]>([]);
  const [touched, setTouched] = useState<string[]>([]);
  const [labels, setLabels] = useState<OptionLabels>({});
  const interacted = useRef(false);
  const labelRequests = useRef<string[]>([]);
  const loadRef = useRef(loadOptions);
  const stepsRef = useRef(steps);
  const rootRef = useRef<HTMLElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const { requestFocus, holdFocus } = useCardFocus(rootRef, titleRef);
  const [stepFocus, setStepFocus] = useState(0);
  const titleId = `alpha-form-${useId().replace(/:/g, '')}`;
  const blockedId = `${titleId}-blocked`;

  useEffect(() => {
    stepsRef.current = steps;
    loadRef.current = loadOptions;
  }, [steps, loadOptions]);

  useEffect(() => {
    if (stepFocus > 0 && focusMayMove(rootRef.current)) {
      headingRef.current?.focus();
    }
  }, [stepFocus]);

  const jumpToErrors = useCallback(
    (fieldErrors: FieldErrors, initial: boolean): void => {
      const target = firstErrorStep(stepsRef.current, fieldErrors);
      if (target < 0 || (!initial && !focusMayMove(rootRef.current))) {
        return;
      }
      setStep(target);
      setFurthest(previous => Math.max(previous, target));
      if (!initial) {
        requestFocus('invalid');
      }
    },
    [requestFocus],
  );

  const actions = useCardActions({
    token,
    fields,
    serverError,
    values,
    submitting,
    cancellingElsewhere,
    onSubmit,
    onCancel,
    submitFailedMessage: SUBMIT_FAILED,
    cancelFailedMessage: CANCEL_FAILED,
    holdFocus,
    onServerFailure: jumpToErrors,
  });
  const { busy, cancelling, failure, serverFieldErrors } = actions;

  useEffect(() => {
    if (interacted.current) {
      writeCardDraft(token, { values, step, furthest });
    }
  }, [token, values, step, furthest]);

  const recordOptions = useCallback(
    (field: FormField, options: FormOption[]): void => {
      setLabels(previous => ({
        ...previous,
        [field.key]: {
          ...previous[field.key],
          ...Object.fromEntries(
            options.map(option => [option.value, option.label]),
          ),
        },
      }));
    },
    [],
  );

  const onReview = step === reviewIndex;
  const held = onReview && blocked;
  const noteShown = held || cancelBlocked;

  useEffect(() => {
    if (!onReview) {
      return;
    }
    const shownNow = visibilityOf(fields, values);
    fields
      .filter(field => {
        const value = getPath(values, field.key);
        return (
          field.optionSource != null &&
          !labelRequests.current.includes(field.key) &&
          shownNow.field(field) &&
          !isBlank(value) &&
          toList(value).some(item => labels[field.key]?.[scalar(item)] == null)
        );
      })
      .forEach(field => {
        const source = field.optionSource ?? '';
        labelRequests.current = [...labelRequests.current, field.key];
        Promise.resolve()
          .then(() => loadRef.current(source, ''))
          .then(options => {
            recordOptions(field, options.slice(0, MAX_OPTIONS));
          })
          .catch(() => undefined);
      });
  }, [onReview, fields, values, labels, recordOptions]);

  const stepErrors = (index: number): FieldErrors =>
    index < reviewIndex
      ? collectErrors(steps[index].fields, values, '', visibility)
      : {};
  const currentErrors = stepErrors(step);
  const shownErrors = {
    ...serverFieldErrors,
    ...(attempted.includes(step)
      ? currentErrors
      : onlyKeys(currentErrors, touched)),
  };
  const validBefore = (index: number): boolean =>
    steps
      .slice(0, index)
      .every(
        item => !hasErrors(collectErrors(item.fields, values, '', visibility)),
      );

  const chips: ChipModel[] = [
    ...steps.map(item => ({ key: item.key, title: item.title })),
    { key: REVIEW_KEY, title: 'Review' },
  ].map((chip, index) => {
    const isReview = index === reviewIndex;
    const current = index === step;
    const serverHit =
      !isReview && hasErrors(errorsFor(steps[index].fields, serverFieldErrors));
    const localHit =
      !isReview &&
      index <= furthest &&
      (!current || attempted.includes(index)) &&
      hasErrors(stepErrors(index));
    const doneStep = !isReview && !current && index <= furthest;
    let state: StepState = 'todo';
    if (serverHit || localHit) {
      state = 'error';
    } else if (doneStep) {
      state = 'done';
    }
    return {
      ...chip,
      state,
      current,
      navigable: !current && (index <= furthest || validBefore(index)),
    };
  });
  const otherAttention = chips.filter(
    chip => chip.state === 'error' && !chip.current,
  ).length;

  const goTo = (index: number, focusHeading = true): void => {
    interacted.current = true;
    setStep(index);
    setFurthest(previous => Math.max(previous, index));
    if (focusHeading) {
      setStepFocus(previous => previous + 1);
    }
  };

  const advance = (): void => {
    if (hasErrors(currentErrors)) {
      setAttempted(previous => addOnce(previous, step));
      requestFocus('invalid');
      return;
    }
    goTo(step + 1);
  };

  const submit = (): void => {
    const invalid = steps.findIndex(item =>
      hasErrors(collectErrors(item.fields, values, '', visibility)),
    );
    if (invalid >= 0) {
      setAttempted(previous => addOnce(previous, invalid));
      goTo(invalid, false);
      requestFocus('invalid');
      return;
    }
    actions.submit(toSubmitValues(fields, values));
  };

  const primaryAction = (): void => {
    if (!onReview) {
      advance();
      return;
    }
    if (!held) {
      submit();
    }
  };

  const cancel = (): void => {
    if (!cancelBlocked) {
      actions.cancel();
    }
  };

  const changeField =
    (field: FormField) =>
    (next: unknown): void => {
      interacted.current = true;
      setValues(previous => setPath(previous, field.key, next));
      actions.markEdited(field.key);
    };

  const touchField = (field: FormField) => (): void => {
    setTouched(previous => addOnce(previous, field.key));
  };

  const submitLabel = card.form?.submitLabel ?? 'Submit';
  const primaryLabel = (): string => {
    if (onReview) {
      return busy ? `${submitLabel}…` : submitLabel;
    }
    return step === reviewIndex - 1 ? 'Review' : 'Next';
  };
  const currentStep = onReview ? undefined : steps[step];
  const stepPrefix = `Step ${step + 1} of ${reviewIndex + 1} · `;
  const changed = onReview ? changedKeys(baseline, values, fields) : [];

  return (
    <CardFrame>
      <CardRoot ref={rootRef} aria-labelledby={titleId} aria-busy={busy}>
        <CardHeader>
          <CardTitle
            ref={titleRef}
            id={titleId}
            tabIndex={-1}
            data-alpha-card-title=""
          >
            {card.form?.title ?? card.title}
          </CardTitle>
          <CardLead>{LEAD}</CardLead>
        </CardHeader>
        <StepChips
          chips={chips}
          onSelect={index => {
            goTo(index);
          }}
        />
        <CardFailureAlert
          failure={failure}
          busy={busy}
          canRetrySubmit={onReview && !held}
          onRetrySubmit={submit}
          onRetryCancel={cancel}
        />
        <StepBody>
          <StepHeading ref={headingRef} tabIndex={-1}>
            {`${stepPrefix}${currentStep?.title ?? 'Review'}`}
          </StepHeading>
          {otherAttention > 0 && (
            <StepAttention>{attentionText(otherAttention)}</StepAttention>
          )}
          {currentStep?.description != null &&
            currentStep.description !== '' && (
              <CardLead>{currentStep.description}</CardLead>
            )}
          {currentStep == null ? (
            <ReviewStep
              steps={steps}
              values={values}
              visibility={visibility}
              labels={labels}
              changed={changed}
              prefill={prefill}
              loadOptions={loadOptions}
              onOptions={recordOptions}
              disabled={busy}
              onEdit={index => {
                goTo(index);
              }}
            />
          ) : (
            <FieldGrid>
              {currentStep.fields
                .filter(field => visibility.field(field))
                .map(field => (
                  <FieldCell key={field.key} wide={isWideField(field)}>
                    <AlphaFieldRenderer
                      field={field}
                      value={getPath(values, field.key)}
                      scope={visibility}
                      errors={shownErrors}
                      disabled={busy}
                      loadOptions={loadOptions}
                      onOptions={recordOptions}
                      prefill={prefill}
                      onChange={changeField(field)}
                      onBlur={touchField(field)}
                    />
                  </FieldCell>
                ))}
            </FieldGrid>
          )}
        </StepBody>
        {onReview && card.notice != null && card.notice !== '' && (
          <Alert severity="info" role="status">
            {card.notice}
          </Alert>
        )}
        {noteShown && (
          <BlockedNote id={blockedId}>{blockedMessage}</BlockedNote>
        )}
        <CardFooter>
          <SecondaryButton
            type="button"
            disabled={busy || cancelling}
            aria-disabled={cancelBlocked || undefined}
            aria-describedby={cancelBlocked ? blockedId : undefined}
            onClick={cancel}
          >
            Cancel
          </SecondaryButton>
          <FooterSpacer />
          {step > 0 && (
            <SecondaryButton
              type="button"
              disabled={busy}
              onClick={() => {
                goTo(step - 1);
              }}
            >
              Back
            </SecondaryButton>
          )}
          <PrimaryButton
            type="button"
            disabled={busy || cancelling}
            aria-disabled={held || undefined}
            aria-describedby={held ? blockedId : undefined}
            onClick={primaryAction}
          >
            {primaryLabel()}
          </PrimaryButton>
        </CardFooter>
      </CardRoot>
    </CardFrame>
  );
};
